import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { signStoragePath } from '@/lib/storagePhoto';
import { useSession } from '@/lib/auth';
import { useActiveLocationId } from '@/lib/locationContext';
import { factsFromRows } from '@/lib/studioFacts';
import { useRealtimeTable } from '@/lib/useRealtimeTable';
import { useFeature } from '@/lib/useFeature';
import { useMyDrawings, people } from '@/lib/drawings';
import { useAcademySessions, whenLabel, daysUntil } from '@/lib/academy';
import { isCell } from '@/lib/cell/config';
import { cellGet } from '@/lib/cell/api';
import { cellSign } from '@/lib/cell/files';
import { DISPLAY_TTL_SECONDS } from '@/lib/storagePhoto';

/**
 * Hub home — the front door of the Two Wings IA (decision aa4fc89d):
 * hero, then the Grow and My Studio wing cards, each carrying live state.
 * Every destination lives INSIDE a wing; the bar and these cards are the
 * only doors Home offers.
 */

type LocationConfig = {
  display_name: string;
  address: string;
  phone: string | null;
  slack_workspace_url: string | null;
};

type HomeData = {
  firstName: string;
  /** The pro's headshot — photo_url (media door) wins, photo_path (legacy bucket) falls back.
   *  Display-only here; changing it lives on My Info. */
  photoUrl: string | null;
  location: LocationConfig | null;
  /** Open maintenance requests at the active location — the live state on
   *  the My Studio wing card ("All quiet" vs "N open"). Same derivation the
   *  wing itself uses. */
  openTickets: number | null;
  /** HUB-074: the reads that failed — a failure is never shown as an empty studio or "All quiet". */
  unread: string[];
  /** Raw spotlight status for the hero card — null when none exists. */
  spotlightStatus: string | null;
};

type CellHome = {
  pro: { id: string; first_name: string | null; photo_path: string | null; photo_url: string | null } | null;
  tenant: { first_name: string | null } | null;
  kb: Array<{ key: string; value: string | null }> | null;
  openTickets: number | null;
  spotlight: { status: string } | null;
};

/** The same HomeData the queryFn below builds, from ONE call on the AWS cell (move map M28). Each read comes back
 *  under its own name and goes through the same mapping; a failed call fails every read, so each is named unread
 *  (HUB-074 — never "All quiet" for a read that did not happen). */
async function cellHome(userId: string, activeLocationId: string | null): Promise<HomeData> {
  const path = activeLocationId
    ? `/v1/pro-hub/locations/${encodeURIComponent(activeLocationId)}/users/${userId}/home`
    : `/v1/pro-hub/users/${userId}/home`;
  // CELL (M28): GET /v1/pro-hub/(locations/{locationId}/)users/{authUserId}/home — pros, tenants, kb, tickets, spotlights.
  const { data, error } = await cellGet<CellHome>(path);
  if (error || !data) {
    return {
      firstName: '', photoUrl: null, location: null, openTickets: activeLocationId ? null : 0, spotlightStatus: null,
      unread: activeLocationId
        ? ['your profile', 'your tenancy', 'your studio’s details', 'your maintenance requests']
        : ['your profile'],
    };
  }
  const { pro } = data;
  let firstName = pro?.first_name ?? '';
  let location: LocationConfig | null = null;
  let openTickets: number | null = 0;
  let spotlightStatus: string | null = null;
  if (pro?.id && activeLocationId) {
    if (data.tenant?.first_name) firstName = data.tenant.first_name;
    const f = factsFromRows(data.kb ?? []);
    location = {
      display_name: f.displayName ?? '',
      address: f.address ?? '',
      phone: f.supportPhone,
      slack_workspace_url: f.communityEnabled ? f.communityUrl : null,
    };
    openTickets = data.openTickets ?? 0;
    spotlightStatus = data.spotlight?.status ?? null;
  }
  // CELL (M28): the legacy ops-photos fallback is signed through /v1/pro-hub/files/sign (the bucket's own rule).
  const signed = !pro?.photo_url && pro?.photo_path
    ? await cellSign('ops-photos', pro.photo_path, DISPLAY_TTL_SECONDS)
    : null;
  const photoUrl = pro?.photo_url ? pro.photo_url : (signed?.data?.signedUrl ?? null);
  return { firstName, photoUrl, location, openTickets, spotlightStatus, unread: [] };
}

export default function Home() {
  const { session } = useSession();
  // Grow's Home card is gated by the same switchboard key as the room itself.
  const growOn = useFeature('grow').enabled;
  // GET ON THE TV (TV-PRO-1, item 19d1b7e2: the Pro Hub's launch hook) leads the home, behind its own switchboard key.
  const tvOn = useFeature('tv').enabled;
  // YOUR DRAWING (PRO-DRAW-1, decision 8d40b94e) leads the home for a pro who hosts one: the count IS the block.
  // Absent when the 'drawings' switch is off or the pro hosts no drawing — never an empty promise.
  const drawings = useMyDrawings().data ?? [];
  const drawing = drawings.find((d) => d.status === 'live') ?? drawings[0] ?? null;
  // NEXT ACADEMY CLASS (ACADEMY-1): one line on Home while the studio's switch is on and a class is coming.
  const nextClass = (useAcademySessions().data ?? [])[0] ?? null;
  const userId = session?.user?.id;
  // The active location is the user's selection (or the auto-default
  // from LocationProvider). All location-scoped queries below use this
  // instead of "most-recently-updated tenant" — that legacy lookup
  // silently dropped data for multi-location Pros (e.g., Jessica
  // Romano @ Demo + Brookline).
  const { activeLocationId } = useActiveLocationId();

  const { data, isLoading, refetch } = useQuery<HomeData>({
    queryKey: ['hub-home', userId, activeLocationId],
    // Don't gate on activeLocationId — operators / non-Pro accounts
    // have no pro_locations rows and would never get a location, so
    // the query would never fire and the page would spin forever.
    // The queryFn handles a null activeLocationId by returning a
    // minimal HomeData with no location-scoped fields.
    enabled: !!userId,
    queryFn: async () => {
      // CELL (M28): the whole load is one endpoint on the AWS cell (cellHome above); Supabase below is untouched.
      if (isCell()) return cellHome(userId!, activeLocationId ?? null);
      const { data: pro, error: proErr } = await supabase
        .from('pros')
        .select('id, first_name, photo_path, photo_url')
        .eq('auth_user_id', userId!)
        .maybeSingle();
      // HUB-074: every read below records its failure instead of posing as "nothing there".
      const unread: string[] = [];
      if (proErr) unread.push('your profile');

      let location: LocationConfig | null = null;
      let firstName = pro?.first_name ?? '';
      let activeTickets: number | null = 0;
      let spotlightStatus: string | null = null;

      if (pro?.id && activeLocationId) {
        // Tenant row at the active location specifically (not "any
        // tenant for this Pro"). For multi-location Pros this is what
        // changes when the selector toggles.
        // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
        const { data: tenant, error: tenantErr } = await supabase
          .from('tenants')
          .select('first_name')
          .eq('pro_id', pro.id)
          .eq('location_id', activeLocationId)
          .maybeSingle();
        if (tenantErr) unread.push('your tenancy');

        if (tenant?.first_name) firstName = tenant.first_name;

        // MY STUDIO IS THE SOURCE (Peter, 2026-08-26). These four facts are an
        // owner's own words, edited in My Studio, which writes location_kb_values
        // — not location_configs, which this read before and which no owner
        // edits. The two had drifted: configs carried a "Suite E" Sharon's own
        // address does not have, and Peter's personal mobile where the studio's
        // support line belongs. See lib/studioFacts.ts.
        // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
        const { data: kb, error: kbErr } = await supabase
          .from('location_kb_values')
          .select('key, value')
          .eq('location_id', activeLocationId)
          .eq('ns', 'location');
        if (kbErr) unread.push('your studio’s details');
        const f = factsFromRows(kb ?? []);
        location = {
          display_name: f.displayName ?? '',
          address: f.address ?? '',
          phone: f.supportPhone,
          // The community channel is My Studio's too, and it is gated on the
          // owner's own toggle — an unset channel offers no dead tile.
          slack_workspace_url: f.communityEnabled ? f.communityUrl : null,
        };

        // Tickets scoped to this Pro at this location (a Pro at two
        // studios has separate ticket queues).
        // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
        const { count: activeCount, error: countErr } = await supabase
          .from('tickets')
          .select('id', { count: 'exact', head: true })
          .eq('pro_id', pro.id)
          .eq('location_id', activeLocationId)
          .in('status', ['open', 'pending', 'in_progress', 'on_hold', 'scheduled']);
        // A failed count is unknown, never "All quiet".
        if (countErr) unread.push('your maintenance requests');
        activeTickets = countErr ? null : (activeCount ?? 0);

        // Spotlight in any non-terminal state contributes to the badge.
        // Scoped to active location — a Pro at two studios may have
        // separate spotlights per location.
        // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
        const { data: sp } = await supabase
          .from('spotlights')
          .select('status')
          .eq('pro_id', pro.id)
          .eq('location_id', activeLocationId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle<{ status: string }>();
        if (sp) {
          spotlightStatus = sp.status;
        }
      }

      // Same resolution ProPhoto uses on My Info: the door's URL (R2 /m/) wins, the legacy
      // ops-photos path is the fallback for pros seeded from Zoho. That fallback is SIGNED with
      // the pro's own session (ops_photos_authed_select), never a public link: ops-photos goes
      // private under security fix #53, and a public link would then load nothing. It is kept
      // rather than dropped because pro-profile-harvest still writes photo_path alone for a
      // newly harvested pro — 0 pros depend on it today (2026-09-25), the next one would.
      const photoUrl = pro?.photo_url
        ? pro.photo_url
        : pro?.photo_path
          ? await signStoragePath('ops-photos', pro.photo_path)
          : null;

      return {
        firstName,
        photoUrl,
        location,
        openTickets: activeTickets,
        spotlightStatus,
        unread,
      };
    },
  });

  // Realtime: the Home cards aggregate two tables (open-maintenance count on
  // the My Studio card, spotlight status on the slot). Filter by location_id
  // only — RLS already restricts to the Pro's own rows on the channel, and
  // the home query refetch is cheap. Without this, a brand-new ticket
  // submitted from another tab wouldn't update the My Studio card's state
  // until the Pro happened to refocus.
  useRealtimeTable({
    table: 'tickets',
    filter: activeLocationId ? `location_id=eq.${activeLocationId}` : undefined,
    invalidateKeys: [['hub-home', userId, activeLocationId]],
    enabled: !!activeLocationId,
  });
  useRealtimeTable({
    table: 'spotlights',
    filter: activeLocationId ? `location_id=eq.${activeLocationId}` : undefined,
    invalidateKeys: [['hub-home', userId, activeLocationId]],
    enabled: !!activeLocationId,
  });

  if (isLoading || !data) {
    return <div className="text-image-slate text-sm">Loading…</div>;
  }

  const { firstName, photoUrl, location, openTickets, spotlightStatus, unread } = data;

  // The card slot below the hero is state-aware.
  // A pro with NO spotlight gets a GENERIC welcome instead (Peter, 2026-08-20: "I may
  // never spotlight some of my pros... something generic that we say to everybody"):
  // the word Spotlight appears nowhere until the owner sends one, so the app never
  // advertises a promise the studio hasn't decided to keep for that person. The day
  // one arrives, this card lights up — being featured is an event, not a queue.
  const spot = spotlightStatus === 'pro_review'
    ? { tag: 'YOUR SPOTLIGHT · DRAFT WAITING', title: 'Your story is drafted', sub: 'Your words, your call — review it and shine.', cta: 'Review & shine', to: '/spotlight' }
    : spotlightStatus && ['submitted', 'drafted', 'approved'].includes(spotlightStatus)
      ? { tag: 'YOUR SPOTLIGHT · IN FLIGHT', title: 'Your story is in motion', sub: "We're on it — see where it stands.", cta: 'See where it stands', to: '/spotlight' }
      : spotlightStatus && ['posted', 'published'].includes(spotlightStatus)
        ? { tag: 'YOUR SPOTLIGHT · LIVE', title: 'Your story is out there', sub: 'Your spotlight is posted — the link lives on My Info.', cta: 'See it', to: '/my-info' }
        : null;

  return (
    <div className="-mx-5 -mt-6">{/* break out of Layout's padding */}
      {unread.length > 0 && (
        <div role="alert" className="mx-5 mt-6 mb-0 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 flex items-center justify-between gap-3" data-testid="home-unread">
          <span>We couldn&rsquo;t load {unread.join(', ')} just now — what&rsquo;s below may be incomplete.</span>
          <button type="button" className="underline shrink-0" onClick={() => { void refetch(); }}>Try again</button>
        </div>
      )}
      {/* OPTION A — "The Room" (Peter, 2026-08-20). The Pro Hub home is styled as
          an IMAGE OS room: warm wash ground, white cards on neutral hairlines, and the
          owner product's signature hero — a dark rounded card with one Didot line and
          a glass studio tag (image-os-web's .roomhero idiom, ported not reinvented).
          The emoji band, the candy pastels and the QR strip are gone on his call. */}
      <div className="max-w-3xl mx-auto px-5 pt-6">
        {/* design/financy · the plum-and-pink gradient becomes the idiom's black hero with its
            colour blobs. Everything on it is unchanged: the studio tag, the pro's own face, the
            greeting and its line. */}
        <div className="fin-hero">
          <div className="fintag">
            {location?.display_name ?? 'IMAGE Studios'}
          </div>
          <div>
            {/* THE PRO'S OWN FACE RIDES THE GREETING (Peter, 2026-08-20: "the photo was
                placed over the name" — the old home carried it and the reskin dropped it).
                Display-only; My Info owns changing it. No photo = no empty ring here. */}
            {photoUrl && (
              <img src={photoUrl} alt="" className="size-24 rounded-full object-cover ring-2 ring-white/50 mb-3" />
            )}
            {/* Generic to EVERY pro on purpose (Peter, 2026-08-20) — no line that reads
                like a feature-me promise. The greeting stays; the flourish is universal. */}
            <h1 className="font-accent text-[26px] sm:text-3xl leading-[1.12]">
              Hey {firstName || 'there'} — your studio, <em className="italic">your day.</em>
            </h1>
            <p className="text-[13px] text-white/75 mt-1.5">Everything you need, right here.</p>
          </div>
        </div>
      </div>

      {/* design/financy · THE THREE DOORS ARE ONE ROW OF BLOCKS. They were three stacked white
          cards, each with a pink Open pill; here each is a whole colour whose meaning is its state.
          Grow stays gated by the 'grow' switchboard key, the studio card keeps its live open-
          maintenance count, and the slot still never disappears — the grid holds whichever of the
          three exist. */}
      {drawing && (
        <div className="max-w-3xl mx-auto px-5 mt-4 fin-blocks one">
          <Link to="/drawings" className="fin-blk green" data-testid="home-drawing">
            <span className="ic" aria-hidden="true">{drawing.entries}</span>
            <span>
              <span className="v">{people(drawing.entries)} signed up.</span>
              <span className="k">Your drawing &middot; {drawing.name}</span>
              <span className="s">See who signed up and get your link to share.</span>
            </span>
          </Link>
        </div>
      )}
      {/* THE NEXT ACADEMY CLASS (ACADEMY-1): corporate's live class, read from its form; the studio texts
          the reminder, this block is the same fact on Home. Lands on My Studio's Academy tab, card lifted. */}
      {nextClass && (
        <div className="max-w-3xl mx-auto px-5 mt-4 fin-blocks one">
          <Link to={`/my-studio?tab=academy&s=${nextClass.id.slice(0, 8)}`} className="fin-blk lav" data-testid="home-academy">
            <span className="ic" aria-hidden="true">{Math.max(0, daysUntil(nextClass.starts_at))}</span>
            <span>
              <span className="v">{nextClass.title}</span>
              <span className="k">Next Academy class &middot; {whenLabel(nextClass.starts_at)}</span>
              <span className="s">{nextClass.going ? 'You\u2019re going. Register if you haven\u2019t yet.' : 'Live online from IMAGE Pro Academy. Register in a minute.'}</span>
            </span>
          </Link>
        </div>
      )}
      {/* GET ON THE TV leads the home when the studio has it on: one wide block above the three doors. */}
      {tvOn && (
        <div className="max-w-3xl mx-auto px-5 mt-4 fin-blocks one">
          <Link to="/tv" className="fin-blk red" data-testid="home-tv">
            <span className="ic" aria-hidden="true">TV</span>
            <span>
              <span className="v">Get on the TV.</span>
              <span className="k">New · The studio&rsquo;s screens</span>
              <span className="s">Send a photo, a short video or a special. The owner approves it, then it plays for everyone who walks in.</span>
            </span>
          </Link>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-5 mt-4 fin-blocks">
        {growOn && (
          <Link to="/grow" className="fin-blk blue">
            <span className="ic" aria-hidden="true">↗</span>
            <span>
              <span className="v">Grow your book.</span>
              <span className="k">New · Marketing</span>
              <span className="s">Your QR code and a running start with your IMAGE Pro app — the tools live here now.</span>
            </span>
          </Link>
        )}

        {/* MY STUDIO's front door — the facility wing's summary card (Two Wings IA, decision
            aa4fc89d). Live state, not a menu: the open-maintenance count is the same derivation the
            wing itself uses, kept fresh by the tickets realtime subscription above. The count IS
            the block's number, and its colour says what the count means: an open request is waiting
            on somebody (yellow), a quiet studio is a thing done (green). */}
        {openTickets === null ? (
          <Link to="/my-studio" className="fin-blk">
            <span className="ic" aria-hidden="true">?</span>
            <span>
              <span className="v">Couldn&rsquo;t check maintenance.</span>
              <span className="k">My Studio</span>
              <span className="s">Open My Studio to see your requests \u00b7 Wi-Fi & door code inside.</span>
            </span>
          </Link>
        ) : (
        <Link to="/my-studio" className={`fin-blk ${openTickets > 0 ? 'yellow' : 'green'}`}>
          <span className="ic" aria-hidden="true">{openTickets > 0 ? '!' : '\u2713'}</span>
          <span>
            <span className="v">
              {openTickets > 0
                ? `${openTickets} open maintenance request${openTickets > 1 ? 's' : ''}.`
                : 'All quiet.'}
            </span>
            <span className="k">My Studio</span>
            <span className="s">
              {openTickets > 0
                ? 'We\u2019re on it \u2014 see where it stands. Wi-Fi & door code inside.'
                : 'No open maintenance \u00b7 Wi-Fi & door code inside.'}
            </span>
          </span>
        </Link>
        )}

        {/* The card slot: the pro's spotlight when one exists, a welcome every pro gets when none
            does — the slot itself never disappears (Peter: "as long as that doesn't go away, I am
            good"). */}
        {spot ? (
          <Link to={spot.to} className="fin-blk lav">
            <span className="ic" aria-hidden="true">{'\u2605'}</span>
            <span>
              <span className="v">{spot.title}</span>
              <span className="k">{spot.tag}</span>
              {/* THE SUB ONLY. `cta` is a BUTTON LABEL, and the Financy look turned this
                  card into a block that appended it to the sentence — so two of the three
                  states stuttered on a pro's own home screen: "We're on it — see where it
                  stands. — See where it stands." The whole card is a <Link>, so the call
                  to action is the card; repeating it in prose only made it read broken. */}
              <span className="s">{spot.sub}</span>
            </span>
          </Link>
        ) : (
          <div className="fin-blk lav">
            <span className="ic" aria-hidden="true">{'\u2302'}</span>
            <span>
              <span className="v">Make yourself at home.</span>
              <span className="k">{(location?.display_name ?? 'YOUR STUDIO').toUpperCase()}</span>
              <span className="s">Maintenance, documents, the Pro Academy, your community {'\u2014'} everything for your day lives right here.</span>
            </span>
          </div>
        )}
      </div>

      {/* Black footer band — IMAGE site signature */}
      {/* design/financy · the band keeps its ink and its words; the pink blur and the
          gradient-clipped text go, because a gradient on type is the one place this idiom does not
          go and the brand mark reads louder as flat colour. */}
      <div className="mt-10 max-w-3xl mx-auto px-5">
        <div className="finfoot">
          <b>
            Your suite. Your brand.{' '}
            <i>YOUR LEGACY.</i>
          </b>
          {location && (
            <p className="text-[11px] tracking-wider uppercase text-white/50 mt-3">
              IMAGE Studios® {location.display_name} · {location.address}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
